import { H1 } from '@payaca/ambrosia';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import useTemplateSaveErrorToast from '@/hooks/useTemplateSaveErrorToast';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';

import CreateEditProposalTemplateControl from '@/ui/components/createEditProposalTemplateControl/CreateEditProposalTemplateControl';
import CreateEditScheduledEventTemplateControl from '@/ui/components/createEditScheduledEventTemplateControl/CreateEditScheduledEventTemplateControl';
import LoaderOverlay from '@payaca/components/loaderOverlay/LoaderOverlay';
import { getUrlSearchParam } from '@payaca/helpers/urlHelper';
import {
  createTemplate,
  getTemplate,
  updateTemplate,
} from '@payaca/store/templates/templatesActions';
import {
  EntityTemplate,
  PublicEntityTemplate,
  TemplatableEntity,
} from '@payaca/types/entity-templates';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

const CreateEditEntityTemplatePage: FC<{
  templatePublicId?: EntityTemplate<TemplatableEntity>['publicId'];
}> = ({ templatePublicId }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();
  const onTemplateSaveError = useTemplateSaveErrorToast();
  const [template, setTemplate] = useState<EntityTemplate<TemplatableEntity>>();

  const [entityType, setEntityType] = useState(
    getUrlSearchParam(location.search, 'entityType')
  );

  useEffect(() => {
    if (templatePublicId) {
      dispatch(
        getTemplate.request({
          publicId: templatePublicId,
          callback: (t) => {
            setTemplate(t);
            setEntityType(t.entityType);
          },
          onErrorCallback: () => {
            history.goBack();
          },
        })
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'dispatch', and 'history'
  }, [templatePublicId]);

  useEffect(() => {
    if (!entityType && !templatePublicId) {
      history.goBack();
    }
    // Task templates are created/edited in the sidebar modal on the templates
    // page — this legacy page no longer renders them.
    if (entityType === 'task') {
      history.replace('/companySettings/operations/task-templates');
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'history'
  }, [templatePublicId, entityType]);

  const title = useMemo(() => {
    let entityLabel = 'proposal';
    if (entityType == 'scheduledEvent') {
      entityLabel = 'event';
    }
    return `${templatePublicId ? 'Edit' : 'Create'} ${entityLabel} template`;
  }, [entityType, templatePublicId]);

  const onRequestSave = useCallback(
    (t: Omit<PublicEntityTemplate<TemplatableEntity>, 'publicId'>) => {
      if (templatePublicId) {
        dispatch(
          updateTemplate.request({
            publicId: templatePublicId,
            template: {
              ...t,
            },
            callback: () => {
              history.goBack();
            },
            onErrorCallback: onTemplateSaveError,
          })
        );
      } else {
        dispatch(
          createTemplate.request({
            template: t,
            callback: () => {
              history.goBack();
            },
            onErrorCallback: onTemplateSaveError,
          })
        );
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'dispatch', and 'history'
    [templatePublicId, onTemplateSaveError]
  );

  return (
    <>
      <AuthenticatedPageWrapper
        breadcrumbItems={[
          {
            label: 'Back',
            to:
              entityType === 'proposal'
                ? '/companySettings/sales-payments/proposal-templates'
                : '/companySettings/operations/event-templates',
          },
          { label: title },
        ]}
      >
        <div className="px-8 py-4">
          <H1>{title}</H1>
        </div>
        {!template && templatePublicId && <LoaderOverlay />}
        <div className="max-w-screen-xl p-6">
          {entityType === 'scheduledEvent' && (
            <CreateEditScheduledEventTemplateControl
              allowModifyTemplateNameDescription={true}
              scheduledEventTemplate={
                template as Omit<
                  PublicEntityTemplate<'scheduledEvent'>,
                  'publicId'
                >
              }
              onRequestSave={onRequestSave}
            />
          )}
          {entityType === 'proposal' && (
            <CreateEditProposalTemplateControl
              allowModifyTemplateNameDescription={true}
              proposalTemplate={
                template as Omit<PublicEntityTemplate<'proposal'>, 'publicId'>
              }
              onRequestSave={onRequestSave}
            />
          )}
        </div>
      </AuthenticatedPageWrapper>
    </>
  );
};

export default CreateEditEntityTemplatePage;
